import { FC, useMemo } from "react";

import {
  ConfigUpdate,
  EvalSpec,
  EvalStats,
} from "@tsmono/inspect-common/types";
import { modelRoleNames } from "@tsmono/inspect-common/utils";
import {
  buildArgsByModel,
  buildArgsByRole,
  buildConfigsByModel,
  buildConfigsByRole,
  fmtClock,
  fmtCompactDuration,
  MetaItem,
  UsagePanel,
} from "@tsmono/inspect-components/usage";

import { EvalLogStatus } from "../../../@types/extraInspect";
import { kLogViewModelsTabId } from "../../../constants";
import { useCurrentLogFile } from "../../routing/currentSelection";
import { useTimelineNavigationForModel } from "../useTimelineNavigation";

// Individual hook for Info tab
export const useModelsTab = (
  evalSpec: EvalSpec | undefined,
  evalStats: EvalStats | undefined,
  evalStatus?: EvalLogStatus,
  configUpdates?: ConfigUpdate[] | null
) => {
  return useMemo(() => {
    return {
      id: kLogViewModelsTabId,
      label: "Models",
      scrollable: true,
      component: ModelTab,
      componentProps: {
        evalSpec,
        evalStats,
        evalStatus,
        configUpdates,
      },
    };
  }, [evalSpec, evalStats, evalStatus, configUpdates]);
};

interface ModelTabProps {
  evalSpec?: EvalSpec;
  evalStats?: EvalStats;
  evalStatus?: EvalLogStatus;
  configUpdates?: ConfigUpdate[] | null;
}

export const ModelTab: FC<ModelTabProps> = ({
  evalSpec,
  evalStats,
  evalStatus,
  configUpdates,
}) => {
  const { href: timelineHref, show: showTimelineForModel } =
    useTimelineNavigationForModel();
  const logFile = useCurrentLogFile();
  const configsByModel = useMemo(
    () => buildConfigsByModel(evalSpec),
    [evalSpec]
  );
  const configsByRole = useMemo(() => buildConfigsByRole(evalSpec), [evalSpec]);
  const argsByModel = useMemo(() => buildArgsByModel(evalSpec), [evalSpec]);
  const argsByRole = useMemo(() => buildArgsByRole(evalSpec), [evalSpec]);

  const roleAliases = useMemo(
    () => modelRoleNames(evalSpec?.model_roles),
    [evalSpec]
  );

  const meta = useMemo<MetaItem[]>(() => {
    const items: MetaItem[] = [];
    const startedAt = evalStats?.started_at;
    const completedAt = evalStats?.completed_at;
    if (startedAt && completedAt) {
      const elapsedSec =
        (new Date(completedAt).getTime() - new Date(startedAt).getTime()) /
        1000;
      if (Number.isFinite(elapsedSec) && elapsedSec >= 0) {
        items.push({
          label: "Elapsed",
          value: fmtCompactDuration(elapsedSec),
        });
      }
    }
    if (startedAt || completedAt) {
      const showDate = !!(
        startedAt &&
        completedAt &&
        new Date(startedAt).toDateString() !==
          new Date(completedAt).toDateString()
      );
      items.push({
        label: "Window",
        value: `${fmtClock(startedAt, showDate)} → ${fmtClock(completedAt, showDate)}`,
      });
    }
    return items;
  }, [evalStats?.started_at, evalStats?.completed_at]);

  const showUsage = evalStatus !== "started";
  const hasModelUsage =
    showUsage &&
    !!evalStats?.model_usage &&
    Object.keys(evalStats.model_usage).length > 0;
  const hasRoleUsage =
    showUsage &&
    !!evalStats?.role_usage &&
    Object.keys(evalStats.role_usage).length > 0;

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          padding: "0.5em 1em 0 1em",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
        }}
      >
        <UsagePanel
          model_usage={hasModelUsage ? evalStats.model_usage : undefined}
          role_usage={hasRoleUsage ? evalStats.role_usage : undefined}
          configs_by_model={configsByModel}
          configs_by_role={configsByRole}
          args_by_model={argsByModel}
          args_by_role={argsByRole}
          role_aliases={roleAliases}
          meta={meta}
          connection_limit_history={evalStats?.connection_limit_history}
          started_at={evalStats?.started_at}
          completed_at={evalStats?.completed_at}
          config_updates={configUpdates}
          main_model={evalSpec?.model}
          state_key={logFile ?? undefined}
          onViewTimeline={showTimelineForModel}
          timelineHref={timelineHref}
        />
      </div>
    </div>
  );
};
